import React, { useState } from 'react';
import { SearchIcon, XIcon } from 'lucide-react';
interface TeamStatsProps {
  activeTab?: string;
}
const TeamStats = ({
  activeTab
}: TeamStatsProps) => {
  const [activeStatTab, setActiveStatTab] = useState('index');
  const [statView, setStatView] = useState('offensive');
  const [radarFactors, setRadarFactors] = useState(['Scoring', 'Defense', 'Rebounding', 'Assists', 'Turnovers']);
  const [newFactor, setNewFactor] = useState('');
  // Suggested factors that aren't currently selected
  const suggestedFactors = ['Pace', 'Free Throws', 'Three Point', 'Field Goals', 'Blocks', 'Steals', 'Efficiency', 'Fast Break'].filter(factor => !radarFactors.includes(factor));
  const statCategories = {
    index: [{
      label: 'Overall Rating',
      value: 89.5,
      percentile: 85
    }, {
      label: 'Offensive Rating',
      value: 112.4,
      percentile: 78
    }, {
      label: 'Defensive Rating',
      value: 108.6,
      percentile: 72
    }, {
      label: 'Net Rating',
      value: 3.8,
      percentile: 80
    }, {
      label: 'Pace',
      value: 98.2,
      percentile: 65
    }],
    scoring: [{
      label: 'PPG',
      value: 115.2,
      percentile: 82
    }, {
      label: 'FG%',
      value: 47.8,
      percentile: 75
    }, {
      label: '3P%',
      value: 36.5,
      percentile: 68
    }, {
      label: 'FT%',
      value: 78.9,
      percentile: 70
    }, {
      label: 'eFG%',
      value: 54.2,
      percentile: 77
    }],
    playmaking: [{
      label: 'APG',
      value: 26.8,
      percentile: 73
    }, {
      label: 'AST%',
      value: 62.4,
      percentile: 75
    }, {
      label: 'TOV%',
      value: 13.2,
      percentile: 45
    }, {
      label: 'AST/TO',
      value: 2.03,
      percentile: 68
    }],
    'stealing and rim': [{
      label: 'STL/G',
      value: 7.8,
      percentile: 65
    }, {
      label: 'BLK/G',
      value: 5.2,
      percentile: 58
    }, {
      label: 'DEF RTG',
      value: 108.6,
      percentile: 72
    }, {
      label: 'OPP FG%',
      value: 45.8,
      percentile: 70
    }],
    rebounding: [{
      label: 'RPG',
      value: 44.5,
      percentile: 67
    }, {
      label: 'OREB',
      value: 10.8,
      percentile: 60
    }, {
      label: 'DREB',
      value: 33.7,
      percentile: 70
    }, {
      label: 'REB%',
      value: 51.2,
      percentile: 65
    }],
    global: [{
      label: 'Win%',
      value: 65.8,
      percentile: 78
    }, {
      label: 'SRS',
      value: 4.2,
      percentile: 75
    }, {
      label: 'MOV',
      value: 6.8,
      percentile: 82
    }, {
      label: 'SOS',
      value: 0.8,
      percentile: 55
    }]
  };
  const getPercentileColor = (percentile: number) => {
    if (percentile >= 70) return 'bg-green-500';
    if (percentile >= 40) return 'bg-orange-400';
    return 'bg-red-500';
  };
  const removeFactor = (factor: string) => {
    setRadarFactors(radarFactors.filter(f => f !== factor));
  };
  const addFactor = (factor?: string) => {
    const factorToAdd = factor || newFactor.trim();
    if (factorToAdd && !radarFactors.includes(factorToAdd)) {
      setRadarFactors([...radarFactors, factorToAdd]);
      setNewFactor('');
    }
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      addFactor();
    }
  };
  return <div data-id="element-890" data-mp="m1k5kjcw0kpl">
      {/* Team Statistics */}
      <div className="bg-white rounded-lg shadow-md" data-id="element-891" data-mp="3kk8z4w5jtwos">
        <div className="p-4 border-b flex justify-between items-center" data-id="element-892" data-mp="3kltu2mvrzhn6">
          <h3 className="font-semibold" data-id="element-893" data-mp="3pk56n5dgg693">TEAM STATISTICS</h3>
          <div className="flex items-center bg-gray-100 rounded-full p-1" data-id="element-894" data-mp="3ui6pbp6yy3k1">
            <button onClick={() => setStatView('offensive')} className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${statView === 'offensive' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-800'}`} data-id="element-895" data-mp="3uk9mquagayys">
              Offensive
            </button>
            <button onClick={() => setStatView('defensive')} className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${statView === 'defensive' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-800'}`} data-id="element-896" data-mp="1drye0024t4tv">
              Defensive
            </button>
          </div>
        </div>
        <div className="p-4" data-id="element-897" data-mp="1mc4r4l8jtmdd">
          {/* Radar Chart Section with Factor Management */}
          <div className="mb-6" data-id="element-898" data-mp="1hanoolaf1ruo">
            <h4 className="font-semibold text-sm mb-3 text-gray-700" data-id="element-899" data-mp="1hdtgi8afj7u3">RADAR</h4>
            <div className="grid grid-cols-2 gap-6" data-id="element-900" data-mp="1v9f4qd8u9nlg">
              {/* Radar Chart */}
              <div className="relative h-64 w-64 mx-auto" data-id="element-901" data-mp="1qbly5j3mirkn">
                <svg viewBox="0 0 180 180" className="w-full h-full" data-id="element-902" data-mp="251ks19o5bgub">
                  {/* Pentagon shape */}
                  <polygon points="90,9 171,67.5 139.5,162 40.5,162 9,67.5" fill="none" stroke="#ccc" strokeWidth="1" data-id="element-903" data-mp="201op7dr6pvgc" />
                  <polygon points="90,36 144,76.5 121.5,135 58.5,135 36,76.5" fill="none" stroke="#ccc" strokeWidth="1" data-id="element-904" data-mp="2e2iwtu6ersdy" />
                  <polygon points="90,63 117,85.5 103.5,108 76.5,108 63,85.5" fill="none" stroke="#ccc" strokeWidth="1" data-id="element-905" data-mp="29y1hc773018z" />
                  {/* Axes */}
                  <line x1="90" y1="9" x2="90" y2="162" stroke="#ccc" strokeWidth="1" data-id="element-906" data-mp="2xnrmy9q785j1" />
                  <line x1="9" y1="67.5" x2="171" y2="67.5" stroke="#ccc" strokeWidth="1" data-id="element-907" data-mp="2jo9speu4w02a" />
                  <line x1="40.5" y1="162" x2="139.5" y2="162" stroke="#ccc" strokeWidth="1" data-id="element-908" data-mp="2oml25ogio8jz" />
                  {/* Data polygon */}
                  <polygon points="90,36 144,76.5 121.5,117 58.5,135 63,76.5" fill="rgba(59, 130, 246, 0.3)" stroke="#3B82F6" strokeWidth="2" data-id="element-909" data-mp="29x5dce0jqd6x" />
                  {/* Labels */}
                  <text x="90" y="4.5" textAnchor="middle" className="text-xs fill-gray-600" data-id="element-910" data-mp="20y1sp5lqdpqb">
                    Scoring
                  </text>
                  <text x="175.5" y="67.5" textAnchor="middle" className="text-xs fill-gray-600" data-id="element-911" data-mp="24z3xe1yf2tzo">
                    Defense
                  </text>
                  <text x="139.5" y="175.5" textAnchor="middle" className="text-xs fill-gray-600" data-id="element-912" data-mp="1vyffgw5wk9hi">
                    Rebounding
                  </text>
                  <text x="40.5" y="175.5" textAnchor="middle" className="text-xs fill-gray-600" data-id="element-913" data-mp="1k1xa6kur0x3">
                    Assists
                  </text>
                  <text x="4.5" y="67.5" textAnchor="end" className="text-xs fill-gray-600" data-id="element-914" data-mp="3ou86tpoq8pgw">
                    Turnovers
                  </text>
                </svg>
              </div>
              {/* Factor Management Column */}
              <div className="flex flex-col" data-id="element-915" data-mp="kg0c5lowr2m6">
                <div className="mb-4" data-id="element-916" data-mp="geoldcuasbce">
                  <label className="text-xs font-semibold text-gray-600 mb-2 block" data-id="element-917" data-mp="gfpxlkn1v3vp">
                    SELECTED FACTORS
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-918" data-mp="6hafu89wx6yv">
                    {radarFactors.map((factor, index) => <div key={index} className="bg-blue-600 text-white px-3 py-1 text-xs rounded-full flex items-center" data-id="element-919" data-mp="2w6ccusqjlbnt" data-mpi={index}>
                        {factor}
                        <XIcon className="w-3 h-3 ml-1 cursor-pointer hover:opacity-75" onClick={() => removeFactor(factor)} data-id="element-920" data-mp="2r5oh5trdc6n9" />
                      </div>)}
                  </div>
                </div>
                <div className="mb-4" data-id="element-921" data-mp="2m2xasblgqv5f">
                  <label className="text-xs font-semibold text-gray-600 mb-2 block" data-id="element-922" data-mp="2r1ifoobnu307">
                    SUGGESTED FACTORS
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-923" data-mp="2vt0fh91y5xjl">
                    {suggestedFactors.map((factor, index) => <button key={index} onClick={() => addFactor(factor)} className="bg-gray-100 text-gray-700 px-3 py-1 text-xs rounded-full hover:bg-gray-200 transition-colors" data-id="element-924" data-mp="2zt0tisl4w6q4" data-mpi={index}>
                        + {factor}
                      </button>)}
                  </div>
                </div>
                <div data-id="element-925" data-mp="3eprv98ia5uiw">
                  <label className="text-xs font-semibold text-gray-600 mb-2 block" data-id="element-926" data-mp="3eqbcso40qgwe">
                    ADD CUSTOM FACTOR
                  </label>
                  <div className="relative" data-id="element-927" data-mp="1kcjxb7z0d2t1">
                    <input type="text" placeholder="Enter factor name" value={newFactor} onChange={e => setNewFactor(e.target.value)} onKeyPress={handleKeyPress} className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm" data-id="element-928" data-mp="1kdbswislv0jb" />
                    <SearchIcon className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" data-id="element-929" data-mp="3h9ogmdo6ig3s" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* All stat categories as individual boxes */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-930" data-mp="33cswnq6e348y">
            {Object.entries(statCategories).map(([category, stats]) => <div key={category} className="border rounded-lg p-4" data-id="element-931" data-mp="2yerfxbwfrh0n">
                <h4 className="font-semibold text-sm mb-3 text-gray-700 capitalize" data-id="element-932" data-mp="3cd7iqrr6gndi">
                  {category === 'index' ? 'Overall' : category}
                </h4>
                <div className="space-y-3" data-id="element-933" data-mp="rlnt3t8x2pug">
                  {stats.map((stat, index) => <div key={index} className="flex flex-col" data-id="element-934" data-mp="nklunemtqhtj" data-mpi={index}>
                      <div className="flex justify-between items-center text-xs mb-1" data-id="element-935" data-mp="nk297nxytbar">
                        <span className="font-medium" data-id="element-936" data-mp="v3bn9uhf1y65">{stat.label}</span>
                        <span className="font-semibold" data-id="element-937" data-mp="103hh9hb5rwre">{stat.value}</span>
                      </div>
                      <div className="w-full h-4 bg-gray-200 rounded-sm overflow-hidden" data-id="element-938" data-mp="l6qfj1e0i8ym">
                        <div className={`h-full ${getPercentileColor(stat.percentile)} flex items-center pl-2 text-white text-xs font-medium`} style={{
                    width: `${stat.percentile}%`
                  }} data-id="element-939" data-mp="q4i71uaqvt2a">
                          {stat.percentile}%
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
};
export default TeamStats;